import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { CustomBranding } from '../types';

interface BrandingManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

const BrandingManager: React.FC<BrandingManagerProps> = ({ isOpen, onClose }) => {
  const { user, userData, refreshUserData } = useAuth();
  const [branding, setBranding] = useState<CustomBranding>({
    enabled: false,
    logoUrl: '',
    backgroundUrl: '',
    primaryColor: '#8B5CF6',
    secondaryColor: '#A78BFA',
    venueName: '',
    splashMessage: 'Up Next',
    watermarkUrl: '',
    fontFamily: 'system-ui'
  });
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    if (userData?.customBranding) {
      setBranding(userData.customBranding);
    }
  }, [userData]);

  const handleSave = async () => {
    if (!user) return;

    setSaving(true);
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        customBranding: branding
      });
      await refreshUserData();
      alert('Branding settings saved successfully!');
    } catch (error) {
      console.error('Error saving branding:', error);
      alert('Failed to save branding settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    setBranding({
      enabled: false,
      logoUrl: '',
      backgroundUrl: '',
      primaryColor: '#8B5CF6',
      secondaryColor: '#A78BFA',
      venueName: '',
      splashMessage: 'Up Next',
      watermarkUrl: '',
      fontFamily: 'system-ui'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto text-white">
        <div className="flex justify-between items-center p-6 border-b border-gray-700">
          <h2 className="text-3xl font-bold">🎨 Custom Branding</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-3xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
            <div>
              <h3 className="text-lg font-semibold">Enable Custom Branding</h3>
              <p className="text-gray-400 text-sm">Apply your custom branding to splash screens</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={branding.enabled}
                onChange={(e) => setBranding({ ...branding, enabled: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {branding.enabled && (
            <>
              {/* Venue Information */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Venue Name
                  </label>
                  <input
                    type="text"
                    value={branding.venueName || ''}
                    onChange={(e) => setBranding({ ...branding, venueName: e.target.value })}
                    placeholder="Your Venue Name"
                    className="w-full p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-qr-primary focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Splash Message
                  </label>
                  <input
                    type="text"
                    value={branding.splashMessage || ''}
                    onChange={(e) => setBranding({ ...branding, splashMessage: e.target.value })}
                    placeholder="Up Next"
                    className="w-full p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-qr-primary focus:outline-none"
                  />
                </div>
              </div>

              {/* Logo and Images */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold">Images</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Logo URL
                  </label>
                  <input
                    type="url"
                    value={branding.logoUrl || ''}
                    onChange={(e) => setBranding({ ...branding, logoUrl: e.target.value })}
                    placeholder="https://your-site.com/logo.png"
                    className="w-full p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-qr-primary focus:outline-none"
                  />
                  <p className="text-gray-400 text-xs mt-1">Recommended: PNG format, transparent background, 400x200px</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Background Image URL
                  </label>
                  <input
                    type="url"
                    value={branding.backgroundUrl || ''}
                    onChange={(e) => setBranding({ ...branding, backgroundUrl: e.target.value })}
                    placeholder="https://your-site.com/background.jpg"
                    className="w-full p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-qr-primary focus:outline-none"
                  />
                  <p className="text-gray-400 text-xs mt-1">Recommended: JPG format, 1920x1080px</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Watermark URL (Optional)
                  </label>
                  <input
                    type="url"
                    value={branding.watermarkUrl || ''}
                    onChange={(e) => setBranding({ ...branding, watermarkUrl: e.target.value })}
                    placeholder="https://your-site.com/watermark.png"
                    className="w-full p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-qr-primary focus:outline-none"
                  />
                  <p className="text-gray-400 text-xs mt-1">Small logo for bottom corner, PNG with transparency</p>
                </div>
              </div>

              {/* Colors */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold">Colors</h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Primary Color
                    </label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={branding.primaryColor || '#8B5CF6'}
                        onChange={(e) => setBranding({ ...branding, primaryColor: e.target.value })}
                        className="w-12 h-10 rounded border border-gray-600 bg-transparent cursor-pointer"
                      />
                      <input
                        type="text"
                        value={branding.primaryColor || ''}
                        onChange={(e) => setBranding({ ...branding, primaryColor: e.target.value })}
                        placeholder="#8B5CF6"
                        className="flex-1 p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-qr-primary focus:outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Secondary Color
                    </label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={branding.secondaryColor || '#A78BFA'}
                        onChange={(e) => setBranding({ ...branding, secondaryColor: e.target.value })}
                        className="w-12 h-10 rounded border border-gray-600 bg-transparent cursor-pointer"
                      />
                      <input
                        type="text"
                        value={branding.secondaryColor || ''}
                        onChange={(e) => setBranding({ ...branding, secondaryColor: e.target.value })}
                        placeholder="#A78BFA"
                        className="flex-1 p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-qr-primary focus:outline-none"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Font */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Font Family
                </label>
                <select
                  value={branding.fontFamily || 'system-ui'}
                  onChange={(e) => setBranding({ ...branding, fontFamily: e.target.value })}
                  className="w-full p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-qr-primary focus:outline-none"
                >
                  <option value="system-ui">System Default</option>
                  <option value="Inter, sans-serif">Inter (Modern)</option>
                  <option value="Roboto, sans-serif">Roboto (Clean)</option>
                  <option value="Montserrat, sans-serif">Montserrat (Bold)</option>
                  <option value="Poppins, sans-serif">Poppins (Friendly)</option>
                  <option value="Oswald, sans-serif">Oswald (Display)</option>
                  <option value="serif">Serif (Classic)</option>
                </select>
              </div>

              {/* Preview */}
              <div className="border border-gray-600 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold">Preview</h3>
                  <button
                    onClick={() => setPreviewMode(!previewMode)}
                    className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    {previewMode ? 'Hide Preview' : 'Show Preview'}
                  </button>
                </div>

                {previewMode && (
                  <div 
                    className="relative w-full h-64 bg-gradient-to-br from-purple-900 to-blue-900 rounded-lg overflow-hidden flex items-center justify-center text-center"
                    style={{
                      backgroundImage: branding.backgroundUrl ? `url(${branding.backgroundUrl})` : undefined,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      fontFamily: branding.fontFamily
                    }}
                  >
                    {branding.logoUrl && (
                      <img 
                        src={branding.logoUrl} 
                        alt="Logo" 
                        className="absolute top-4 left-4 h-12 object-contain"
                      />
                    )}
                    
                    <div className="bg-black/50 p-6 rounded-lg">
                      <div 
                        className="text-2xl font-bold mb-2"
                        style={{ color: branding.primaryColor }}
                      >
                        {branding.splashMessage || 'Up Next'}
                      </div>
                      <div className="text-xl text-white mb-1">Sample Song Title</div>
                      <div 
                        className="text-lg"
                        style={{ color: branding.secondaryColor }}
                      >
                        Singer: John Doe
                      </div>
                      {branding.venueName && (
                        <div className="text-sm text-gray-300 mt-2">
                          {branding.venueName}
                        </div>
                      )}
                    </div>

                    {branding.watermarkUrl && (
                      <img 
                        src={branding.watermarkUrl} 
                        alt="Watermark" 
                        className="absolute bottom-4 right-4 h-8 object-contain opacity-70"
                      />
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Branding Tips */}
          <div className="bg-blue-900/30 border border-blue-600/50 rounded-lg p-4">
            <h4 className="font-semibold text-blue-200 mb-2">🎨 Branding Tips</h4>
            <ul className="text-blue-100 text-sm space-y-1">
              <li>• Use high-resolution images for best quality on large screens</li>
              <li>• PNG format with transparency works best for logos</li>
              <li>• Test your colors on both dark and light backgrounds</li>
              <li>• Keep text readable with good contrast</li>
              <li>• Watermarks should be subtle and not distract from content</li>
            </ul>
          </div>
        </div>

        <div className="flex justify-between p-6 border-t border-gray-700">
          <button
            onClick={resetToDefaults}
            className="bg-gray-600 hover:bg-gray-700 px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            Reset to Defaults
          </button>
          
          <div className="flex space-x-4">
            <button
              onClick={onClose}
              className="bg-gray-600 hover:bg-gray-700 px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-qr-primary hover:bg-qr-secondary disabled:bg-gray-600 px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              {saving ? 'Saving...' : 'Save Branding'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandingManager;